import type { ApiSubscribe, ApiSubscription } from "@/backend/natsstubs/wire/ApiSubscriber";
import type { Sticker } from "@/model/StickerType";
import type { BoardType } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import type { Iteration, SessionAlmStatus, Team } from "@/model/session";
import type { StickyType } from "@/model/stickyType";
import type { AuthUser } from "@/model/user";
import type { EventFilter } from "@/store/activity";
import type { StatusItem } from "@/store/search/searchMenu";

import type { GetDevLinksResponse } from "../natsstubs/almsync/models";
import type { GetSignalsResponse } from "../natsstubs/board/models";
import type {
  BurndownDataPoint,
  CapacityAllocationItem,
  CfdDay,
  ChartScope,
  DependencyFlowItem,
  GetPredictabilityResponse,
} from "../natsstubs/session/models";
import type { SendMessageRequest, SendMessageResponse } from "../natsstubs/sightline/models";
import type {
  ServerAlmItemType,
  ServerAlmItemTypeMap,
  ServerAlmSource,
  ServerAlmStickyTypeData,
  ServerAlmType,
  ServerArt,
  ServerBoard,
  ServerBoardEventsResponse,
  ServerBoardIteration,
  ServerCategory,
  ServerCompanySettings,
  ServerEvent,
  ServerFlexBoard,
  ServerFlexType,
  ServerInfo,
  ServerKanbanConfig,
  ServerKanbanConfigMap,
  ServerLicense,
  ServerLink,
  ServerObjectives,
  ServerSearchResult,
  ServerSession,
  ServerSettings,
  ServerStatusWithData,
  ServerStickiesUpdate,
  ServerSticky,
  ServerStickyChange,
  ServerStickyGroupBoardIds,
  ServerStickerType,
  ServerStickyType,
} from "../serverModel";
import type { EventInfo } from "./EventInfo";

export const subscriptionLevels = ["room", "session", "board"] as const;
export type SubscriptionLevel = (typeof subscriptionLevels)[number];

export interface SearchStickiesQuery {
  text?: string;
  teams?: Team[];
  types?: StickyType[];
  flags?: Array<Sticker | null>;
  statuses?: StatusItem[];
  boards?: Board[];
  iterations?: Iteration[];
  assignees?: AuthUser[];
  reporters?: AuthUser[];
}

export interface Subscription {
  drain(): Promise<void>;

  unsubscribe(): void;

  getID(): string | number;

  getSubject(): string;
}

export function isSubscription(s: Subscription | ApiSubscription): s is Subscription {
  return "drain" in s;
}

export type SubscriptionHandler<TArgs, TKwargs = unknown> = (
  args: TArgs,
  kwargs: TKwargs,
  event: EventInfo,
) => void | Promise<void>;

export interface Unsubscribable {
  unsubscribe: () => void;
}

export interface BackendReadSession {
  initSession(id: string): Promise<boolean>;

  pauseSubscriptions(pause: boolean): void;

  apiSubscribe<T>(
    subscribe: ApiSubscribe<T>,
    handler: NoInfer<T>,
    params?: Record<string, string>,
    level?: SubscriptionLevel,
  ): ApiSubscription[];

  roomSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>): void;

  // The subscription is session scoped, so on session switch it will be unsubscribed automatically.
  // For optional manual early unsubscribe, use the returned Unsubscribable.
  pubsubSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>): Unsubscribable;

  sessionSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>): void;

  boardSubscribe<TArgs>(
    boardId: string,
    path: string,
    level: SubscriptionLevel,
    handler: SubscriptionHandler<TArgs>,
  ): void;

  almSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>): void;

  unsubscribeLevel(level: SubscriptionLevel, connectionOpen?: boolean): Promise<void>;

  unsubscribe(subscription: Subscription): Promise<void>;

  subscriptionNames(level: SubscriptionLevel): string[];

  getAlmSource(
    boardType: ServerBoard["board_type"],
    { teamId, artId }: { teamId?: ServerBoard["user_id"]; artId: ServerBoard["art_id"] },
  ): ServerAlmSource | undefined;

  getAlmType(): ServerAlmType;

  getAlmSyncId(): string | undefined;

  getAlmConnectionId(): string | undefined;

  getAlmStatus(): Promise<SessionAlmStatus>;

  getAlmItemType(
    almType: string,
    boardType: BoardType,
    boardKeys: string[],
  ): Promise<ServerAlmItemTypeMap>;

  getAlmItemTypeForSticky(stickyAlmId: string): Promise<ServerAlmItemType>;

  getAlmStickyTypes(): Promise<ServerAlmStickyTypeData[]>;

  hasMappedAlmUser(id?: string): Promise<boolean>;

  isManualAlmUserMappingAllowed(): Promise<boolean>;

  getLicense(): Promise<ServerLicense>;

  getServerInfo(useHttp: boolean, timeout?: number): Promise<ServerInfo>;

  getStatusWithData(sizeMB: number, useHttp?: boolean): Promise<ServerStatusWithData>;

  getSessions(): Promise<ServerSession[]>;

  getSettings(): Promise<ServerSettings>;

  getCompanySettings(): Promise<ServerCompanySettings>;

  getArts(sessionId?: string): Promise<ServerArt[]>;

  getBoardIterations(
    withStatusDistribution: boolean,
    boardId?: string,
  ): Promise<Record<string, ServerBoardIteration[]>>;

  getLinks(): Promise<ServerLink[]>;

  getBoards(sessionId?: string): Promise<Array<ServerBoard | ServerFlexBoard>>;

  getScale(boardId: string): Promise<number>;

  getBoardStickies(
    boardId: string,
    since?: number,
    withStatusDistribution?: boolean,
  ): Promise<ServerSticky[] | ServerStickiesUpdate>;

  getStickies(
    stickyIds: string[],
    withLinks?: boolean,
    withStatusDistribution?: boolean,
  ): Promise<ServerSticky[]>;

  getStickyTypes(): Promise<ServerStickyType[]>;

  getStickerTypes(): Promise<ServerStickerType[]>;

  getObjectives(boardId: string, enriched: boolean): Promise<ServerObjectives>;

  getFlexTypes(sessionId?: string): Promise<ServerFlexType[]>;

  getCategories(): Promise<ServerCategory[]>;

  searchStickies(query: SearchStickiesQuery): Promise<ServerSearchResult[]>;

  getEvents(): Promise<ServerEvent[]>;

  getBoardIdsOfGroupedStickies(id: string): Promise<ServerStickyGroupBoardIds>;

  getStickyChanges(id: string): Promise<ServerStickyChange[]>;

  getBoardState(id: string, time: Date): Promise<ServerSticky[]>;

  getBoardDiff(
    id: string,
    from: Date,
    to: Date,
    filters?: EventFilter[],
    changeTypes?: string[],
  ): Promise<ServerStickyChange[]>;

  getBoardEventLog(
    id: string,
    from: Date,
    to: Date,
    filters?: EventFilter[],
    changeTypes?: string[],
  ): Promise<ServerBoardEventsResponse>;

  getStatusDistribution(params: {
    boardId?: string;
    teamId?: string;
    iterationId?: number;
    artId?: number;
    stickyId?: string;
    objectiveId?: string;
  }): Promise<
    Array<{ statusClass: string; statusName: string; totalStoryPoints: number; itemsCount: number }>
  >;

  getSignals(boardId: string): Promise<GetSignalsResponse>;

  getCapacityAllocation(
    teamIds: number[],
  ): Promise<{ data: CapacityAllocationItem[]; warnings: string[] }>;
  getDependencyFlow(
    teamIds: number[],
    iterationId: number | null,
  ): Promise<{ data: DependencyFlowItem[]; warnings: string[] }>;
  getBurndownChartData(teamIds: number[], scope: ChartScope): Promise<BurndownDataPoint[]>;
  getCfdChartData(teamIds: [number, ...number[]], scope: ChartScope): Promise<CfdDay[]>;
  getPredictability(
    teamIds: [number, ...number[]],
    includeArchived: boolean,
  ): Promise<GetPredictabilityResponse>;

  getCachedKanbanConfigs(teamIds: string[]): Promise<ServerKanbanConfigMap>;

  getAlmKanbanConfigs(teamIds: string[]): Promise<ServerKanbanConfigMap>;

  getKanbanConfig(artId: number, force: boolean): Promise<ServerKanbanConfig | null>;

  getDevLinks(issueKey: string): Promise<GetDevLinksResponse["devLinks"]>;

  sendSightlineMessage(request: SendMessageRequest): Promise<SendMessageResponse>;

  stopSightlineMessage(messageId: string): Promise<void>;
}
